import type { ChangeEvent } from "react";
import { Fragment, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    Pagination,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useBrandDelete } from "src/api/client-api/brand/useDeleteBrand";
import { CompanyAutocompleteSingle, CompanyOption } from "src/components/Selector/CompanySelector";
import { useNavigate } from "react-router";
import { FoodsIcon } from "@foodpilot/foods";
import { isBrandCollectionError, useGetAllBrands } from "src/api/client-api/brand/useGetAllBrand";
import { useCreateBrand } from "src/api/client-api/brand/useCreateBrand";
import type { Brand, BrandList as BrandListItem, BrandOrganization } from "src/api/client-api/brand/brandSchemas";
import { useGetOneBrand } from "src/api/client-api/brand/useGetOneBrand";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { BrandAccessSection, type BrandAccessValues } from "./BrandAccessSection";
import { BrandAccessLabel, BrandRowActions } from "./BrandTableCells";
import {
    buildBrandCreation,
    buildBrandPatch,
    getAccessViolationMessage,
    getBrandAccessSummary,
    type BrandFormValues,
} from "./brandAccess";
import { getActiveBrandCollection } from "./brandListRemoteState";
import { BrandCollectionStateError } from "src/api/client-api/brand/BrandCollectionStateError";

// Create search hook for brands
const useSearchBrands = (searchTerm: string, options?: { enabled?: boolean }) => {
    return useGetAllBrands({
        name: searchTerm,
        itemsPerPage: 100,
        ...options,
    });
};

// Delete Confirmation Modal Component
//
// Renders both deletion steps:
//   - "confirm"  : the standard "are you sure?" prompt issued on the first
//                  click, with no knowledge of the brand's product usage.
//   - "in-use"   : escalated when the backend returns a 409 because products
//                  still reference the brand. Confirming this step retries
//                  with `force=true`, which dissociates those products
//                  (sets product.brand_id = NULL) before the brand is removed.
type DeleteModalStep = "confirm" | "in-use";

const DeleteBrandModal = ({
    open,
    step,
    productCount,
    onClose,
    onConfirm,
    isDeleting,
}: {
    open: boolean;
    step: DeleteModalStep;
    productCount: number;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting: boolean;
}) => {
    const { t } = useTranslation();

    const isInUse = step === "in-use";
    const title = isInUse ? t("brands.inUseTitle", "Brand in use") : t("brands.confirmDelete", "Confirm Delete");
    const headerSubtitle =
        isInUse ?
            t("brands.inUseDescription", "Deleting will remove the brand from every linked product.")
        :   t("brands.deleteDescription", "This action cannot be undone.");
    // We pass `count` so the i18n layer can interpolate (and pluralize once
    // proper plural keys are added). The English fallback below mirrors what
    // an i18n:sync run would emit, so the UI degrades gracefully if the key
    // is missing from the locale files.
    const bodyMessage =
        isInUse ?
            t(
                "brands.inUseWarning",
                "{{count}} products are currently using this brand. Deleting it will remove the brand from those products.",
                { count: productCount },
            )
        :   t("brands.deleteWarning", "Are you sure you want to delete this brand? This action cannot be undone.");
    const confirmLabel = isInUse ? t("brands.deleteAnyway", "Delete anyway") : t("brands.delete", "Delete");

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {title}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {headerSubtitle}
                    </Typography>
                </Box>
                <Box
                    onClick={onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                }}
            >
                <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36", textAlign: "center" }}>
                    {bodyMessage}
                </Typography>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isDeleting}
                    sx={{
                        minWidth: "100px",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    {t("brands.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={isDeleting}
                    sx={(theme) => ({
                        minWidth: "100px",
                        height: "40px",
                        padding: "0 16px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            opacity: 0.36,
                        },
                    })}
                >
                    {isDeleting ?
                        <CircularProgress size={20} color="inherit" />
                    :   confirmLabel}
                </Button>
            </Box>
        </Dialog>
    );
};

const organizationIdOf = (organization: BrandOrganization) => parseInt(organization["@id"].split("/").pop() ?? "0");

const toOwnerOption = (organization: BrandOrganization): CompanyOption => ({
    "@id": organization["@id"],
    id: organizationIdOf(organization),
    name: organization.name,
});

const brandToFormValues = (brand?: Brand | null): BrandFormValues => ({
    name: brand?.name ?? "",
    organization: brand?.organization["@id"],
    sharedWithOrganizations: brand?.sharedWithOrganizations.map((organization) => organization["@id"]) ?? [],
    sharedWithAllSubsidiaries: brand?.sharedWithAllSubsidiaries ?? false,
});

export const EditBrandModal = ({
    brandId,
    onClose,
    onSuccess,
}: {
    brandId: number;
    onClose: () => void;
    onSuccess: () => void;
}) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const { brand, isError, error } = useGetOneBrand(String(brandId));
    const failedToLoad = isError && brand === undefined && !(isAxiosError(error) && error.response?.status === 401);

    useEffect(() => {
        if (failedToLoad) {
            setSnackbarProps({ message: t("common.error_occurred", "An error occurred"), type: "error" });
            onClose();
        }
    }, [failedToLoad, onClose, setSnackbarProps, t]);

    if (brand === undefined) {
        return (
            <Dialog open onClose={onClose}>
                <Box sx={{ display: "flex", justifyContent: "center", padding: "32px" }}>
                    <CircularProgress aria-label={t("word.loading_wait", "Loading…")} />
                </Box>
            </Dialog>
        );
    }

    return <BrandModal open onClose={onClose} onSuccess={onSuccess} editingBrand={brand} />;
};

const BrandModal = ({
    open,
    onClose,
    onSuccess,
    editingBrand,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingBrand?: Brand | null;
}) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const [initialValues] = useState(() => brandToFormValues(editingBrand));
    const [name, setName] = useState(initialValues.name);
    const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(() =>
        editingBrand ? toOwnerOption(editingBrand.organization) : null,
    );
    const [access, setAccess] = useState<BrandAccessValues>({
        sharedWithOrganizations: initialValues.sharedWithOrganizations,
        sharedWithAllSubsidiaries: initialValues.sharedWithAllSubsidiaries,
    });
    const [accessError, setAccessError] = useState<string | null>(null);

    const createBrandMutation = useCreateBrand();
    const isCreating = createBrandMutation.isLoading;

    const isEditing = !!editingBrand;

    const changeOwner = (owner: CompanyOption | null) => {
        if (owner?.["@id"] !== selectedCompany?.["@id"]) {
            const sharedWithOrganizations =
                owner?.["@id"] === initialValues.organization ? initialValues.sharedWithOrganizations : [];
            setAccess((current) => ({ ...current, sharedWithOrganizations }));
        }
        setSelectedCompany(owner);
        setAccessError(null);
    };

    const changeAccess = (values: BrandAccessValues) => {
        setAccess(values);
        setAccessError(null);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setSnackbarProps({
                message: t("brands.nameRequired", "Name is required"),
                type: "error",
            });
            return;
        }

        const owner = selectedCompany?.["@id"];
        if (owner === undefined) {
            return;
        }

        const values: BrandFormValues = { name: name.trim(), organization: owner, ...access };

        try {
            await createBrandMutation.mutateAsync({
                brand:
                    editingBrand ?
                        buildBrandPatch(editingBrand.id, initialValues, values)
                    :   buildBrandCreation({ ...values, organization: owner }),
            });

            setSnackbarProps({
                message:
                    isEditing ?
                        t("brands.updateSuccess", "Brand updated successfully")
                    :   t("brands.createSuccess", "Brand created successfully"),
                type: "success",
            });

            onSuccess();
            onClose();
        } catch (error) {
            const accessViolationMessage = getAccessViolationMessage(error);
            if (accessViolationMessage !== null) {
                setAccessError(accessViolationMessage);
                return;
            }

            setSnackbarProps({
                message:
                    isEditing ?
                        t("brands.updateError", "Failed to update brand")
                    :   t("brands.createError", "Failed to create brand"),
                type: "error",
            });
        }
    };

    const isFormValid = name.trim().length > 0 && selectedCompany !== null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ? t("brands.editTitle", "Edit Brand") : t("brands.createTitle", "Create Brand")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("brands.editDescription", "Update the parameters for your brand.")
                        :   t("brands.createDescription", "Set the parameters for your brand.")}
                    </Typography>
                </Box>
                <Box
                    onClick={onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                    overflowY: "auto",
                }}
            >
                <Stack spacing={3}>
                    {/* Name Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("brands.nameLabel", "Name")}:
                        </Typography>
                        <TextField
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("brands.namePlaceholder", "Enter brand name")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": {
                                    fontSize: "0.875rem",
                                    padding: "0px 16px",
                                    color: "#393A36",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {/* Company Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("brands.ownerOrganization", "Owner company")}:
                        </Typography>
                        <CompanyAutocompleteSingle
                            value={selectedCompany}
                            onChange={changeOwner}
                            placeholder={t("brands.companyPlaceholder", "Select a company")}
                        />
                        <Typography
                            variant="small-regular"
                            sx={(theme) => ({ color: theme.custom.grey[2200], marginTop: theme.spacing(1) })}
                        >
                            {t("brands.ownerOrganizationHint", "Only this company can edit or delete the brand.")}
                        </Typography>
                    </Box>
                    <BrandAccessSection
                        owner={
                            selectedCompany === null ? null : (
                                { iri: selectedCompany["@id"], id: selectedCompany.id, name: selectedCompany.name }
                            )
                        }
                        sharedWithOrganizations={access.sharedWithOrganizations}
                        sharedWithAllSubsidiaries={access.sharedWithAllSubsidiaries}
                        onChange={changeAccess}
                        errorMessage={accessError}
                    />
                </Stack>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={handleSave}
                    disabled={!isFormValid || isCreating}
                    sx={(theme) => ({
                        width: "150px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        opacity: isFormValid ? 1 : 0.36,
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                        },
                    })}
                >
                    {isCreating ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("brands.update", "Update")
                    :   t("brands.save", "Save")}
                </Button>
            </Box>
        </Dialog>
    );
};

const BrandsTable = ({
    brands,
    viewerOrganizationIri,
    onEdit,
    onDelete,
}: {
    brands: BrandListItem[];
    viewerOrganizationIri: IRI | undefined;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
}) => {
    const { t } = useTranslation();

    if (brands.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "8px",
                    overflow: "hidden",
                }}
            >
                {/* Table Header */}
                <Box
                    sx={{
                        backgroundColor: "#FEFCFB",
                        borderBottom: "1px solid #E7E6E4",
                        display: "flex",
                    }}
                >
                    <Box
                        sx={{
                            width: "300px",
                            padding: "0px 14px 0px 20px",
                            height: "48px",
                            display: "flex",
                            alignItems: "center",
                            borderRight: "1px solid #E7E6E4",
                        }}
                    >
                        <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                            {t("brands.name", "Name")}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "200px",
                            padding: "0px 14px 0px 20px",
                            height: "48px",
                            display: "flex",
                            alignItems: "center",
                            borderRight: "1px solid #E7E6E4",
                        }}
                    >
                        <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                            {t("brands.ownerOrganization", "Owner company")}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "200px",
                            padding: "0px 14px 0px 20px",
                            height: "48px",
                            display: "flex",
                            alignItems: "center",
                            borderRight: "1px solid #E7E6E4",
                        }}
                    >
                        <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                            {t("brands.access.column", "Access")}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100px",
                            padding: "0px 14px 0px 20px",
                            height: "48px",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                            {t("brands.actions", "Actions")}
                        </Typography>
                    </Box>
                </Box>

                {/* Empty State */}
                <Box
                    sx={{
                        height: "130px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#393A36", textAlign: "center" }}>
                        {t("brands.noBrands", "No brands found")}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid #E7E6E4",
                borderRadius: "8px",
                overflow: "hidden",
            }}
        >
            <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: "#FEFCFB",
                            "& .MuiTableCell-root": {
                                borderBottom: "1px solid #E7E6E4",
                                borderRight: "1px solid #E7E6E4",
                                padding: "0px 14px 0px 20px",
                                height: "48px",
                                "&:last-child": {
                                    borderRight: "none",
                                },
                            },
                        }}
                    >
                        <TableCell sx={{ width: "300px" }}>
                            <Typography variant="small-medium" sx={{ lineHeight: "20px", color: "#767673" }}>
                                {t("brands.name", "Name")}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: "200px" }}>
                            <Typography variant="small-medium" sx={{ lineHeight: "20px", color: "#767673" }}>
                                {t("brands.ownerOrganization", "Owner company")}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: "200px" }}>
                            <Typography variant="small-medium" sx={{ lineHeight: "20px", color: "#767673" }}>
                                {t("brands.access.column", "Access")}
                            </Typography>
                        </TableCell>
                        <TableCell
                            sx={{
                                width: "100px",
                                minWidth: "100px",
                                maxWidth: "100px",
                            }}
                        >
                            <Typography variant="small-medium" sx={{ lineHeight: "20px", color: "#767673" }}>
                                {t("brands.actions", "Actions")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {brands.map((brand, index) => (
                        <Fragment key={brand.id}>
                            <TableRow
                                sx={{
                                    "& .MuiTableCell-root": {
                                        borderRight: "1px solid #E7E6E4",
                                        padding: "0px 14px 0px 20px",
                                        height: "58px",
                                        "&:last-child": {
                                            borderRight: "none",
                                            padding: "0px 8px",
                                            width: "100px",
                                            minWidth: "100px",
                                            maxWidth: "100px",
                                        },
                                    },
                                    "&:hover": {
                                        backgroundColor: "#F9F9F8",
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        padding: "0px 20px",
                                        height: "58px",
                                        borderBottom: "none",
                                    }}
                                >
                                    <Typography variant="small-medium" sx={{ lineHeight: "22px", color: "#1E1E1E" }}>
                                        {brand.name}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none" }}>
                                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                                        {brand.organization.name}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none" }}>
                                    <BrandAccessLabel summary={getBrandAccessSummary(brand, viewerOrganizationIri)} />
                                </TableCell>
                                <TableCell
                                    sx={{
                                        borderBottom: "none",
                                    }}
                                >
                                    <BrandRowActions
                                        brandName={brand.name}
                                        canModify={brand.canModify}
                                        canDelete={brand.canDelete}
                                        onEdit={() => onEdit(brand.id)}
                                        onDelete={() => onDelete(brand.id)}
                                    />
                                </TableCell>
                            </TableRow>
                            {/* Separator line */}
                            {index < brands.length - 1 && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ padding: "0px 24px", height: "1px" }}>
                                        <Box
                                            sx={{
                                                height: "1px",
                                                backgroundColor: "#E7E6E4",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
};

export const BrandList = () => {
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();
    const navigate = useNavigate();
    const { setSnackbarProps } = useSnackbar();

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Search state
    const { search, delayedSearch, setSearch } = useDelayedSearch({
        actionOnDelay: () => setPage(1),
    });

    // Delete dialog state. The same dialog renders both steps:
    //   - "confirm": initial "are you sure?" prompt.
    //   - "in-use" : escalated step shown only when the backend returned a
    //                409 because products still reference the brand.
    // `productCount` is populated from the parsed 409 message and used to
    // tell the user how many products will be detached on a force delete.
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        brandId: number | null;
        step: DeleteModalStep;
        productCount: number;
    }>({ open: false, brandId: null, step: "confirm", productCount: 0 });

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBrandId, setEditingBrandId] = useState<number | null>(null);

    // Use the brands hooks - switch between search and all based on search term
    const allBrandsQuery = useGetAllBrands({
        page,
        itemsPerPage: limit,
    });
    const searchBrandsQuery = useSearchBrands(delayedSearch, {
        enabled: delayedSearch.length >= 3,
    });

    const isSearchActive = delayedSearch.length >= 3;
    const {
        state: brandCollectionState,
        items: brands,
        totalItems,
        refetch,
    } = getActiveBrandCollection({
        isSearchActive,
        listQuery: allBrandsQuery,
        searchQuery: searchBrandsQuery,
    });
    const isLoading = brandCollectionState.status === "loading";
    const isError = isBrandCollectionError(brandCollectionState);
    const isSuccess = brandCollectionState.status === "success-value" || brandCollectionState.status === "empty";

    const deleteBrandMutation = useBrandDelete();
    const isDeleting = deleteBrandMutation.isLoading;

    const transformedBrands: BrandListItem[] | undefined = brands;

    // Calculate pagination for search results (client-side pagination)
    const startIndex = isSearchActive ? (page - 1) * limit : 0;
    const endIndex = isSearchActive ? startIndex + limit : transformedBrands?.length;
    const paginatedBrands =
        transformedBrands === undefined ? undefined
        : isSearchActive ? transformedBrands.slice(startIndex, endIndex)
        : transformedBrands;

    // Calculate total pages
    const totalPages = totalItems === undefined ? undefined : Math.ceil(totalItems / limit);

    const handleCreateBrand = () => {
        setEditingBrandId(null);
        setModalOpen(true);
    };

    const handleModalSuccess = () => {
        refetch();
    };

    const handleEditBrand = (id: number) => {
        setEditingBrandId(id);
        setModalOpen(true);
    };

    const handleDeleteClick = (id: number) => {
        setDeleteDialog({ open: true, brandId: id, step: "confirm", productCount: 0 });
    };

    // Parse the structured 409 message
    //   `Brand "X" is currently used by N products.`
    // emitted by BrandProcessor. Falls back to 0 if the wording shifts so the
    // UI degrades gracefully without lying to the user.
    const extractProductCount = (message: unknown): number => {
        if (typeof message !== "string") return 0;
        const match = message.match(/used by (\d+) products?/i);
        return match ? parseInt(match[1], 10) : 0;
    };

    const performDelete = async (brandId: number, force: boolean) => {
        try {
            await deleteBrandMutation.mutateAsync({ id: brandId, force });

            setSnackbarProps({
                message: t("brands.deleteSuccess", "Brand deleted successfully"),
                type: "success",
            });

            setDeleteDialog({ open: false, brandId: null, step: "confirm", productCount: 0 });
            refetch();
        } catch (error) {
            const apiError = error as { response?: { status?: number; data?: { detail?: string } } };

            // 409 only happens on the "confirm" step: the brand still has
            // products. Escalate to the in-use step so the user can confirm a
            // destructive force delete that also detaches the products.
            if (!force && apiError?.response?.status === 409) {
                const productCount = extractProductCount(apiError.response.data?.detail);
                setDeleteDialog((prev) => (prev.brandId === null ? prev : { ...prev, step: "in-use", productCount }));
                return;
            }

            setSnackbarProps({
                message: t("brands.deleteError", "Failed to delete brand"),
                type: "error",
            });
            setDeleteDialog({ open: false, brandId: null, step: "confirm", productCount: 0 });
        }
    };

    const handleDeleteConfirm = () => {
        if (deleteDialog.brandId === null) return;
        const force = deleteDialog.step === "in-use";
        void performDelete(deleteDialog.brandId, force);
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, brandId: null, step: "confirm", productCount: 0 });
    };

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleLimitChange = (event: SelectChangeEvent<number>) => {
        setLimit(event.target.value as number);
        setPage(1);
    };

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Box sx={{ marginLeft: "16px" }}>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t("brands.title", "Brands")}
                        </Typography>
                    </Box>
                </Box>

                {/* Search and Create Section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "24px 32px",
                        backgroundColor: "white",
                    }}
                >
                    <Box sx={{ width: "323px" }}>
                        <TextField
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("brands.searchPlaceholder", "Search...")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiInputBase-input": {
                                    fontWeight: 500,
                                    fontSize: "0.875rem",
                                    lineHeight: "1.6em",
                                    color: "#393A36",
                                    padding: "0px 10px 0px 20px",
                                    "&::placeholder": {
                                        color: "#A3A2A0",
                                        opacity: 1,
                                    },
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <Box
                                        sx={{
                                            width: "16px",
                                            height: "16px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: "12px",
                                        }}
                                    >
                                        <FoodsIcon icon="search" size={1} />
                                    </Box>
                                ),
                            }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<FoodsIcon icon="plus" size={2} />}
                        onClick={handleCreateBrand}
                        sx={(theme) => ({
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                color: "white !important",
                            },
                            "&:disabled": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                opacity: 0.36,
                                color: "white !important",
                            },
                        })}
                    >
                        {t("brands.create", "Add a Brand")}
                    </Button>
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "16px 32px 24px 32px" }}>
                    {isLoading ?
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                            <CircularProgress />
                        </Box>
                    : isError ?
                        <BrandCollectionStateError state={brandCollectionState} onRetry={refetch} />
                    : isSuccess && paginatedBrands !== undefined && totalItems !== undefined ?
                        <>
                            <BrandsTable
                                brands={paginatedBrands}
                                viewerOrganizationIri={selectedCompany?.["@id"]}
                                onEdit={handleEditBrand}
                                onDelete={handleDeleteClick}
                            />

                            {/* Pagination */}
                            {totalPages !== undefined && totalPages > 1 && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mt: 3 }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body2">
                                            {t("brands.rowsPerPage", "Rows per page")}:
                                        </Typography>
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: 80,
                                                "& .MuiOutlinedInput-root": {
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: (theme) => theme.palette.primary[600],
                                                    },
                                                },
                                            }}
                                        >
                                            <Select value={limit} onChange={handleLimitChange}>
                                                <MenuItem value={5}>5</MenuItem>
                                                <MenuItem value={10}>10</MenuItem>
                                                <MenuItem value={25}>25</MenuItem>
                                                <MenuItem value={50}>50</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        showFirstButton
                                        showLastButton
                                        sx={{
                                            "& .MuiPaginationItem-root": {
                                                color: "#393A36",
                                            },
                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                backgroundColor: (theme) => theme.palette.primary[600],
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: (theme) => theme.palette.primary[700],
                                                },
                                            },
                                        }}
                                    />

                                    <Typography variant="body2">
                                        {t("brands.showing", "Showing")} {Math.min((page - 1) * limit + 1, totalItems)}-
                                        {Math.min(page * limit, totalItems)} {t("brands.of", "of")} {totalItems}
                                    </Typography>
                                </Stack>
                            )}
                        </>
                    :   null}
                </Box>
            </Paper>

            {/* Brand Modal */}
            {modalOpen &&
                (editingBrandId === null ?
                    <BrandModal key="create" open onClose={() => setModalOpen(false)} onSuccess={handleModalSuccess} />
                :   <EditBrandModal
                        key={editingBrandId}
                        brandId={editingBrandId}
                        onClose={() => setModalOpen(false)}
                        onSuccess={handleModalSuccess}
                    />)}

            {/* Delete Confirmation Modal */}
            <DeleteBrandModal
                open={deleteDialog.open}
                step={deleteDialog.step}
                productCount={deleteDialog.productCount}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
            />
        </Box>
    );
};
